import { useAuthStore } from "../stores/authStore";
export interface Permission {
  resource: string;
  action: string;
  roles: string[];
}

export const usePermissions = () => {
  const { user } = useAuthStore();

  const leadership = ["admin", "director", "head_teacher"];
  // The finance approval chain: they are school officers, not academic staff,
  // so they sit beside leadership rather than inside it.
  const financeOfficers = ["principal", "cfo"];
  const academicStaff = [
    ...leadership,
    "director_of_studies",
    "head_of_department",
    "class_teacher",
    "teacher",
  ];
  const employeeRoles = [
    ...academicStaff,
    ...financeOfficers,
    "school_doctor",
    "hostel_matron",
    "hostel_custodian",
    "kitchen_manager",
    "cafeteria_manager",
    "sports_master",
    "uniform_manager",
    "inventory_manager",
    "school_counsellor",
    "librarian",
    "bursar",
    "transport_manager",
    "bus_driver",
    "disciplinary_committee",
  ];

  const hasRole = (roles: string | string[]): boolean => {
    if (!user) return false;
    const userRoles = Array.isArray(roles) ? roles : [roles];
    return userRoles.includes(user.user_type);
  };

  /** Checks the caller's resolved capabilities (role default + admin
   * overrides), not a hardcoded role list -- use this for financial/workflow
   * actions instead of hasRole/canAccess, which stay role-based for now. */
  const hasCapability = (slugs: string | string[]): boolean => {
    if (!user?.capabilities) return false;
    const required = Array.isArray(slugs) ? slugs : [slugs];
    return required.some((slug) => user.capabilities!.includes(slug));
  };

  const hasPermission = (permission: Permission): boolean => {
    if (!user) return false;
    return permission.roles.includes(user.user_type);
  };

  // NOTE: the action is accepted but not yet consulted -- access is decided by
  // the resource alone, so canAccess("students", "write") answers the same as
  // canAccess("students", "read"). Callers in ProtectedRoute and
  // RoleBasedComponent already pass an action and get read-level treatment.
  // Kept in the signature so those call sites and the documented API stay
  // valid; underscored so it does not read as an oversight.
  const canAccess = (resource: string, _action: string = "read"): boolean => {
    if (!user) return false;
    // Define resource-based permissions
    const resourcePermissions: Record<string, string[]> = {
      students: employeeRoles,
      financial: ["admin", "director", "principal", "cfo", "bursar"],
      // Only those who review or approve documents: the page needs one of those capabilities.
      "my-approvals": ["admin", "principal", "cfo"],
      permissions: ["admin"],
      payments: [...leadership, "bursar", "parent"],
      // HR is the principal's job and the CFO signs salary changes, so both
      // need the staff area. The head teacher is not on this list: a teacher's
      // pay is not their business, and what they teach is in Academics and
      // Timetable. What anyone may do once here is decided by capability.
      staff: ["admin", "director", "principal", "cfo"],
      "hr-setup": ["admin", "director", "principal"],
      // Everyone the school employs has an appraisal, so everyone the school
      // employs reaches the page. Which appraisals they can actually open is
      // decided server-side: their own, the ones they supervise, the ones they
      // approve, or the whole register with access.appraisal.view_all.
      appraisals: employeeRoles,
      "appraisal-templates": ["admin", "director", "principal"],
      analytics: [...leadership, "director_of_studies"],
      administration: leadership,
      library: [
        ...leadership,
        "librarian",
        "teacher",
        "class_teacher",
        "student",
        "parent",
      ],
      medical: [...leadership, "school_doctor"],
      transport: [
        ...leadership,
        "transport_manager",
        "bus_driver",
        "parent",
        "student",
      ],
      hostel: [
        ...leadership,
        "hostel_matron",
        "hostel_custodian",
        "parent",
        "student",
      ],
      sports: [
        "admin",
        "director",
        "head_teacher",
        "sports_master",
        "teacher",
        "class_teacher",
        "parent",
        "student",
      ],
      communication: [...employeeRoles, "parent", "student"],
      events: [...employeeRoles, "parent", "student"],
      bulletin: [...employeeRoles, "parent", "student"],
      disciplinary: [...academicStaff, "disciplinary_committee"],
      counseling: ["admin", "school_counsellor"],
      notifications: [...employeeRoles, "parent", "student"],
      portal: ["parent", "student"],
      attendance: academicStaff,
      academics: academicStaff,
      reports: [...academicStaff, "principal", "cfo", "bursar"],
      curriculum: academicStaff,
      syllabi: academicStaff,
      "class-setup": [
        "admin",
        "director",
        "head_teacher",
        "director_of_studies",
      ],
      parents: ["admin", "director", "head_teacher"],
      examinations: academicStaff,
      timetable: [...academicStaff, "parent", "student"],
      // The CFO opens Inventory for the fixed assets alone, to post their entries.
      inventory: [...leadership, "inventory_manager", "cfo"],
      facilities: employeeRoles,
      cafeteria: [
        ...leadership,
        "kitchen_manager",
        "cafeteria_manager",
        "parent",
        "student",
      ],
      scholarships: [...leadership, "bursar", "parent", "student"],
      meetings: [...academicStaff, "parent"],
      emergency: [...employeeRoles, "parent", "student"],
      uniforms: [...leadership, "uniform_manager", "parent", "student"],
      alumni: [...employeeRoles, "parent", "student"],
      "student-lifecycle": [...leadership, "director_of_studies"],
    };

    const allowedRoles = resourcePermissions[resource];
    return allowedRoles ? allowedRoles.includes(user.user_type) : false;
  };

  const getFilteredNavigation = (navigationItems: any[]) => {
    return navigationItems.filter((item) => {
      if (!item.roles) return true;
      return hasRole(item.roles);
    });
  };

  return {
    user,
    hasRole,
    hasCapability,
    hasPermission,
    canAccess,
    getFilteredNavigation,
    isAdmin: hasRole("admin"),
    isStaff: hasRole(employeeRoles),
    isTeacher: hasRole("teacher"),
    isParent: hasRole("parent"),
    isStudent: hasRole("student"),
    isBursar: hasRole("bursar"),
    isLibrarian: hasRole("librarian"),
    isNurse: false,
    isDoctor: hasRole("school_doctor"),
    isMedicalStaff: hasRole("school_doctor"),
    isTransportManager: hasRole("transport_manager"),
    isHostelManager: hasRole("hostel_manager"),
    isSportsCoach: hasRole("sports_master"),
    isCounselor: hasRole("school_counsellor"),
    isInventoryManager: hasRole("inventory_manager"),
    isCafeteriaManager: hasRole("cafeteria_manager"),
    isUniformManager: hasRole("uniform_manager"),
  };
};
